import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import {
  bandCounts,
  bandFor,
  bandValue,
  parsePastedGrid,
  parseScoreCell,
  studentName,
  type Assessment,
  type Measure,
  type Score,
  type Student,
  type Term,
} from '@td/shared';
import type { SaveScores, ScoreWrite } from '../hooks/useAssessments';
import { ApiRequestError } from '../lib/api';
import { usePrivacy } from '../privacy/PrivacyContext';

/**
 * A class by measure grid.
 *
 * Deliberately a plain table of inputs rather than a spreadsheet component:
 * the whole job is twenty-five rows and a handful of columns, and a teacher
 * needs it to behave like the boxes on a paper record sheet — type, press
 * Enter, move down.
 */
export function ScoreGrid({
  assessment,
  students,
  scores,
  term,
  date,
  save,
  onSaved,
  readOnly = false,
}: {
  assessment: Assessment;
  students: Student[];
  scores: Score[];
  term: Term | null;
  date: string;
  save: SaveScores;
  onSaved: () => void;
  /** The archive shows the same grid with the typing taken out. */
  readOnly?: boolean;
}) {
  const { privacy } = usePrivacy();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [pasteNote, setPasteNote] = useState<string | null>(null);
  const gridRef = useRef<HTMLTableElement>(null);

  // Scores for the term (or date) on screen, keyed for O(1) lookup per cell.
  const byCell = useMemo(() => {
    const map = new Map<string, Score>();
    for (const s of scores) {
      if (term ? s.term !== term : s.date !== date) continue;
      map.set(`${s.studentId}:${s.measureId}`, s);
    }
    return map;
  }, [scores, term, date]);

  useEffect(() => setDraft({}), [assessment.id, term, date]);

  const cellKey = (studentId: string, measureId: string) => `${studentId}:${measureId}`;

  function valueFor(studentId: string, measure: Measure): string {
    const key = cellKey(studentId, measure.id);
    if (key in draft) return draft[key]!;
    const score = byCell.get(key);
    if (!score) return '';
    if (measure.scoreType === 'level') return score.level ?? '';
    return score.value == null ? '' : String(score.value);
  }

  /**
   * A termly score is identified by its term, so it carries no date — sending
   * an empty one was rejected outright, which silently swallowed every score
   * typed into a BOY/MOY/EOY grid.
   */
  function when(): { term: Term | null; date?: string } {
    return term ? { term } : { term: null, date };
  }

  function commit(studentId: string, measure: Measure, raw: string) {
    const key = cellKey(studentId, measure.id);
    const existing = byCell.get(key);
    const trimmed = raw.trim();

    const write: ScoreWrite =
      measure.scoreType === 'level'
        ? { measureId: measure.id, studentId, ...when(), level: trimmed || null }
        : { measureId: measure.id, studentId, ...when(), value: parseScoreCell(trimmed) };

    // Nothing changed — don't spend a request on a cell that was tabbed past.
    const same =
      measure.scoreType === 'level'
        ? (existing?.level ?? null) === (write.level ?? null)
        : (existing?.value ?? null) === (write.value ?? null);

    setDraft((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });

    if (!same) save.mutate([write], { onSuccess: onSaved });
  }

  /**
   * Paste a block from Excel or Sheets.
   *
   * The paste lands wherever the cursor is and fills down and right from
   * there, which is what happens in a spreadsheet and therefore what a teacher
   * expects. Names in the first column are ignored rather than matched — the
   * order on screen is the order they copied, and guessing at name matching
   * would put scores against the wrong child.
   */
  function onPaste(e: ClipboardEvent, rowIndex: number, colIndex: number) {
    const text = e.clipboardData.getData('text/plain');
    if (!text.includes('\t') && !text.includes('\n')) return;

    e.preventDefault();
    const rows = parsePastedGrid(text);
    const writes: ScoreWrite[] = [];

    rows.forEach((cells, r) => {
      const student = students[rowIndex + r];
      if (!student) return;

      cells.forEach((cell, c) => {
        const measure = assessment.measures[colIndex + c];
        if (!measure) return;

        writes.push(
          measure.scoreType === 'level'
            ? { measureId: measure.id, studentId: student.id, ...when(), level: cell || null }
            : {
                measureId: measure.id,
                studentId: student.id,
                ...when(),
                value: parseScoreCell(cell),
              },
        );
      });
    });

    if (writes.length === 0) return;
    save.mutate(writes, { onSuccess: onSaved });

    const rowsUsed = Math.min(rows.length, students.length - rowIndex);
    setPasteNote(
      `Pasted ${writes.length} score${writes.length === 1 ? '' : 's'} across ${rowsUsed} student${rowsUsed === 1 ? '' : 's'}.` +
        (rows.length > rowsUsed
          ? ` ${rows.length - rowsUsed} extra row(s) had nowhere to go.`
          : ''),
    );
  }

  /** Enter and the arrows move down a row, the way a record sheet is filled. */
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) {
    const move = (dr: number, dc: number) => {
      const next = gridRef.current?.querySelector<HTMLInputElement>(
        `[data-cell="${rowIndex + dr}-${colIndex + dc}"]`,
      );
      if (next) {
        e.preventDefault();
        next.focus();
        next.select();
      }
    };

    if (e.key === 'Enter' || e.key === 'ArrowDown') move(1, 0);
    else if (e.key === 'ArrowUp') move(-1, 0);
    else if (
      e.key === 'ArrowRight' &&
      e.currentTarget.selectionStart === e.currentTarget.value.length
    )
      move(0, 1);
    else if (e.key === 'ArrowLeft' && e.currentTarget.selectionStart === 0) move(0, -1);
  }

  if (students.length === 0) {
    return <p className="text-sm text-stone">Add students to the roster first.</p>;
  }

  return (
    <div>
      {save.isError && (
        <div className="mb-3 rounded-card border border-rust bg-rose/20 px-3 py-2 text-sm text-rust print:hidden">
          That score didn't save.{' '}
          {save.error instanceof ApiRequestError ? save.error.message : 'Try again in a moment.'}
        </div>
      )}

      {pasteNote && (
        <div className="mb-3 flex items-center gap-2 rounded-card border border-sage bg-sage/15 px-3 py-2 text-sm text-espresso print:hidden">
          <span className="flex-1">{pasteNote}</span>
          <button onClick={() => setPasteNote(null)} className="btn-soft">
            Got it
          </button>
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-4 print:hidden">
        {assessment.measures.map((measure) =>
          measure.bands.length === 0 ? null : (
            <div key={measure.id} className="text-[11px]">
              <p className="mb-1 text-stone">{measure.name}</p>
              <div className="flex flex-wrap gap-1">
                {bandCounts(
                  [...byCell.values()].filter((s) => s.measureId === measure.id),
                  measure,
                ).map(({ band, count }) => (
                  <span
                    key={band.label}
                    className="rounded px-1.5 py-0.5"
                    style={{ backgroundColor: `${band.color}2E`, color: band.color }}
                  >
                    {band.label} {count}
                  </span>
                ))}
              </div>
            </div>
          ),
        )}
      </div>

      <div className="overflow-x-auto">
        <table
          ref={gridRef}
          className="w-full min-w-[520px] border-collapse overflow-hidden rounded-card bg-white shadow-soft print:shadow-none"
        >
          <thead>
            <tr className="bg-linen">
              <th className="sticky left-0 z-10 w-48 border-b border-r border-sand bg-linen px-2 py-2 text-left text-xs font-normal text-walnut">
                Student
              </th>
              {assessment.measures.map((m) => (
                <th
                  key={m.id}
                  className="border-b border-r border-sand px-2 py-2 text-left text-xs font-normal text-walnut"
                >
                  {m.name}
                  {m.unit && <span className="ml-1 text-stone">({m.unit})</span>}
                  {m.scoreType === 'percent' && m.outOf && (
                    <span className="ml-1 text-stone">/{m.outOf}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {students.map((student, rowIndex) => (
              <tr key={student.id}>
                <th className="sticky left-0 z-10 border-b border-r border-sand bg-white px-2 py-1 text-left text-xs font-normal">
                  <span className="text-stone">{student.number ?? '—'}</span>{' '}
                  <span className="text-espresso">
                    {privacy
                      ? `${studentName(student)[0]}. ${student.lastName[0]}.`
                      : `${studentName(student)} ${student.lastName}`}
                  </span>
                </th>

                {assessment.measures.map((measure, colIndex) => {
                  const key = cellKey(student.id, measure.id);
                  const score = byCell.get(key);
                  const band = bandFor(bandValue(score, measure), measure.bands);

                  return (
                    <td key={measure.id} className="border-b border-r border-sand p-0">
                      <input
                        data-cell={`${rowIndex}-${colIndex}`}
                        className="w-full bg-transparent px-2 py-1.5 text-sm text-espresso outline-none focus:bg-linen/60"
                        style={
                          band
                            ? { backgroundColor: `${band.color}24`, color: band.color }
                            : undefined
                        }
                        title={band?.label}
                        inputMode={measure.scoreType === 'level' ? 'text' : 'decimal'}
                        value={valueFor(student.id, measure)}
                        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                        onBlur={(e) => commit(student.id, measure, e.target.value)}
                        onKeyDown={(e) => onKeyDown(e, rowIndex, colIndex)}
                        onPaste={(e) => onPaste(e, rowIndex, colIndex)}
                        readOnly={readOnly}
                        aria-label={`${measure.name} for ${studentName(student)} ${student.lastName}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-[11px] text-stone print:hidden">
        {readOnly ? 'A record of a past year. Nothing here can be changed.' : null}
      </p>

      <p className="mt-2 text-[11px] text-stone print:hidden" hidden={readOnly}>
        Type and press Enter to move down. Paste a block from a spreadsheet and it fills from the
        cell you're in — the order on screen is the order it lands in, so sort your spreadsheet the
        same way first.
      </p>
    </div>
  );
}
